import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';



export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  useEffect(() => {
    const handleSession = async () => {
      // ✅ Method 1: Let Supabase handle it automatically via onAuthStateChange
      const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'PASSWORD_RECOVERY') {
          // Supabase automatically detected the recovery token
          setSessionReady(true);
          subscription.unsubscribe();
          return;
        }
      });

      // ✅ Method 2: Manual hash parsing (fallback from Feed.jsx navigate)
      const hash = location.state?.hash || window.location.hash;
      if (hash && hash.includes('access_token')) {
        const params = new URLSearchParams(hash.replace('#', ''));
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        const type = params.get('type');

        if (accessToken && refreshToken && type === 'recovery') {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            setError('Invalid or expired reset link. Please request a new one.');
            subscription.unsubscribe();
          } else {
            setSessionReady(true);
            subscription.unsubscribe();
          }
          return;
        }
      }

      // ✅ Method 3: Check if there's already an active session
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setSessionReady(true);
        subscription.unsubscribe();
        return;
      }

      // No token found at all
      setTimeout(() => {
        setError(prev => prev || 'Invalid or expired reset link. Please request a new one.');
        subscription.unsubscribe();
      }, 3000);
    };

    handleSession();
  }, []);

  const handleReset = async (e) => {
    e.preventDefault();
    if (!password.trim()) { setError('Password is required'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }

    setLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) { setError(error.message); return; }
      setSuccess(true);
      setTimeout(() => navigate('/'), 3000);
    } catch (err) {
      setError('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    background: 'rgba(255,255,255,0.04)',
    border: '0.5px solid rgba(212,83,126,0.3)',
    borderRadius: 10, padding: '11px 14px',
    fontSize: 14, color: '#f5e6ed',
    marginBottom: 10, boxSizing: 'border-box',
    outline: 'none',
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#080608',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: '#0e0a0c', border: '0.5px solid rgba(212,83,126,0.3)',
        borderRadius: 18, width: '100%', maxWidth: 380, padding: 32,
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: 'linear-gradient(135deg, #D4537E, #993556)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, margin: '0 auto 14px', color: '#fff',
          }}>🔑</div>
          <h2 style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 300, fontSize: 24,
            letterSpacing: '0.05em', color: '#f5e6ed', marginBottom: 6,
          }}>Reset Password</h2>
          <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.4)' }}>
            Enter your new password below
          </p>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>✅</div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: '#f5e6ed', marginBottom: 8 }}>
              Password updated!
            </p>
            <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.4)' }}>
              Redirecting you to the feed...
            </p>
          </div>
        ) : !sessionReady ? (
          <div style={{ textAlign: 'center' }}>
            {error ? (
              <>
                <div style={{ fontSize: 44, marginBottom: 14 }}>😕</div>
                <p style={{ fontSize: 13, color: '#f87171', marginBottom: 20 }}>{error}</p>
                <button onClick={() => navigate('/')} style={{
                  width: '100%', background: '#D4537E', color: '#fff',
                  borderRadius: 10, padding: '12px', fontSize: 14,
                  border: 'none', cursor: 'pointer', fontWeight: 600,
                }}>Go to feed</button>
              </>
            ) : (
              <p style={{ fontSize: 12, color: 'rgba(245,230,237,0.4)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                Verifying link...
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleReset}>
            <input
              type="password" value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="New password..."
              autoFocus style={inputStyle}
              onFocus={e => e.target.style.borderColor = '#D4537E'}
              onBlur={e => e.target.style.borderColor = 'rgba(212,83,126,0.3)'}
            />
            <input
              type="password" value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Confirm new password..."
              style={{ ...inputStyle, marginBottom: error ? 6 : 16 }}
              onFocus={e => e.target.style.borderColor = '#D4537E'}
              onBlur={e => e.target.style.borderColor = 'rgba(212,83,126,0.3)'}
            />
            {error && (
              <p style={{ fontSize: 12, color: '#f87171', marginBottom: 12 }}>{error}</p>
            )}
            <button type="submit" disabled={loading} style={{
              width: '100%', background: '#D4537E', color: '#fff',
              borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 600,
              border: 'none', cursor: 'pointer',
              opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s',
            }}>
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}